import { useCallback, useEffect, useReducer } from 'react';
import { PayoutMethodsForm, PayoutMethodsFormState } from './types';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchPayoutAccountAction,
  fetchPayoutAccountSuccessAction,
  showNotifyFail,
  showNotifySuccess,
  updatePayoutAccountAction
} from '../../modules/actions';
import { Api } from '../../modules/utils/API';
import { getPayoutAccountSelector } from '../../modules/selectors';
import { payoutMethodsReducer } from './payout-methods-reducer';
import { IPayoutAccount } from '../../modules/types/user';
import { IUpdatePayoutAccountParams } from '../../modules/types';
import { createPayoutAccount, updatePayoutAccount } from '../../modules/sagas/user';
import { canEnableIBAN, canEnablePayPal } from './common';
import { getTranslationKey } from '../../modules/utils';

function _usePayouMethods(): Omit<PayoutMethodsForm, 'submit'> & {
  setSubmitting: (value: boolean) => void;
} {
  const payoutAccount = useSelector(getPayoutAccountSelector);
  const [state, dispatch] = useReducer(payoutMethodsReducer, {
    submitting: false,
    paypal: {
      local: {
        payPalEnabled: false,
        payPalClientId: '',
        payPalSecret: ''
      },
      validation: {
        payPalEnabled: '',
        payPalClientId: '',
        payPalSecret: ''
      },
      ui: {
        edit: true,
        showErrors: false,
        focused: false
      }
    },
    iban: {
      local: {
        ibanEnabled: false,
        iban: '',
        accountHolder: ''
      },
      validation: {
        ibanEnabled: '',
        iban: '',
        accountHolder: ''
      },
      ui: {
        edit: true,
        showErrors: false,
        focused: false
      }
    }
  });

  const appDispatch = useDispatch();
  useEffect(() => {
    appDispatch(fetchPayoutAccountAction());
  }, []);

  return {
    payoutAccountId: payoutAccount.id || null,
    submitting: state.submitting,
    setSubmitting: (value: boolean) => dispatch({ type: 'setSubmitting', value }),
    paypal: {
      ...state.paypal,
      remote: {
        payPalEnabled: payoutAccount.payPalEnabled,
        payPalClientId: payoutAccount.payPalClientId,
        payPalSecret: payoutAccount.payPalSecret
      },
      enable: async () => {
        const isActivatingPaypal = !payoutAccount.payPalEnabled;
        dispatch({
          type: 'changePayPalFieldValue',
          field: 'payPalEnabled',
          value: isActivatingPaypal
        });
        if (!state.paypal.ui.edit) {
          const response = await Api.User.updatePayoutAccount(
            { payPalEnabled: isActivatingPaypal },
            payoutAccount.id
          );
          if (response.code) {
            dispatch({
              type: 'changePayPalFieldValue',
              field: 'payPalEnabled',
              value: !isActivatingPaypal
            });
            appDispatch(
              isActivatingPaypal
                ? showNotifyFail({
                    message: getTranslationKey('shop.settings.payPalActivationFailed')
                  })
                : showNotifyFail()
            );
          } else {
            appDispatch(fetchPayoutAccountSuccessAction(response.result));
            appDispatch(
              isActivatingPaypal
                ? showNotifySuccess({
                    message: getTranslationKey('shop.settings.payPalActivationSuccess')
                  })
                : showNotifySuccess({})
            );
          }
        }
      },
      enablingAllowed: () => true,
      submissionAllowed: () =>
        state.paypal.ui.edit &&
        !!state.paypal.local.payPalClientId &&
        !!state.paypal.local.payPalSecret,
      toggleEditMode: (edit?: boolean) => {
        dispatch({
          type: 'toggleEditMode',
          method: 'paypal',
          edit
        });
      },
      toggleErrors: (show?: boolean) => {
        dispatch({
          type: 'toggleErrors',
          method: 'paypal',
          show
        });
      },
      changeField: (field, value) => {
        dispatch({
          type: 'changePayPalFieldValue',
          field,
          value
        });
      },
      setFields: (fields) => {
        dispatch({
          type: 'setPayPalFields',
          ...fields
        });
      },
      setValidation: (validation) => {
        dispatch({
          type: 'setPayPalValidation',
          ...validation
        });
      },
      setFocused: (focused) => {
        dispatch({
          type: 'setFocused',
          method: 'paypal',
          focused
        });
      }
    },
    iban: {
      ...state.iban,
      remote: {
        ibanEnabled: payoutAccount.ibanEnabled,
        iban: payoutAccount.ibanNew,
        accountHolder: payoutAccount.accountHolder
      },
      enable: () => {
        dispatch({
          type: 'changeIBANFieldValue',
          field: 'ibanEnabled',
          value: !state.iban.local.ibanEnabled
        });
        if (!state.iban.ui.edit) {
          appDispatch(
            updatePayoutAccountAction({
              id: payoutAccount.id,
              ibanEnabled: !payoutAccount.ibanEnabled
            })
          );
        }
      },
      enablingAllowed: () => true,
      submissionAllowed: () =>
        state.iban.ui.edit && !!state.iban.local.accountHolder && !!state.iban.local.iban,
      toggleEditMode: (edit?: boolean) => {
        dispatch({
          type: 'toggleEditMode',
          method: 'iban',
          edit
        });
      },
      toggleErrors: (show?: boolean) => {
        dispatch({
          type: 'toggleErrors',
          method: 'iban',
          show
        });
      },
      changeField: (field, value) => {
        dispatch({
          type: 'changeIBANFieldValue',
          field,
          value
        });
      },
      setFields: (fields) => {
        dispatch({
          type: 'setIBANFields',
          ...fields
        });
      },
      setValidation: (validation) => {
        dispatch({
          type: 'setIBANValidation',
          ...validation
        });
      },
      setFocused: (focused) => {
        dispatch({
          type: 'setFocused',
          method: 'iban',
          focused
        });
      }
    }
  };
}

export function usePayoutMethods(): PayoutMethodsForm {
  const appDispatch = useDispatch();
  const payoutMethods = _usePayouMethods();

  useEffect(() => {
    payoutMethods.paypal.setFields({
      payPalEnabled:
        payoutMethods.paypal.local.payPalEnabled || payoutMethods.paypal.remote.payPalEnabled,
      payPalClientId:
        payoutMethods.paypal.local.payPalClientId ||
        payoutMethods.paypal.remote.payPalClientId ||
        '',
      payPalSecret: '' // user must enter PayPal secret from scratch if they want to change it
    });
    payoutMethods.iban.setFields({
      ibanEnabled: payoutMethods.iban.local.ibanEnabled || payoutMethods.iban.remote.ibanEnabled,
      accountHolder:
        payoutMethods.iban.local.accountHolder || payoutMethods.iban.remote.accountHolder || '',
      iban: payoutMethods.iban.local.iban || payoutMethods.iban.remote.iban || ''
    });

    if (!canEnablePayPal(payoutMethods.paypal.remote)) {
      payoutMethods.paypal.toggleEditMode(true);
    } else {
      payoutMethods.paypal.toggleEditMode(false);
    }

    if (!canEnableIBAN(payoutMethods.iban.remote)) {
      payoutMethods.iban.toggleEditMode(true);
    } else {
      payoutMethods.iban.toggleEditMode(false);
    }
  }, [
    payoutMethods.paypal.remote.payPalEnabled,
    payoutMethods.paypal.remote.payPalClientId,
    payoutMethods.paypal.remote.payPalSecret,
    payoutMethods.iban.remote.ibanEnabled,
    payoutMethods.iban.remote.accountHolder,
    payoutMethods.iban.remote.iban
  ]);

  const submit = useCallback(async () => {
    const update: Omit<IUpdatePayoutAccountParams, 'id'> = {};

    if (
      payoutMethods.paypal.ui.edit &&
      (payoutMethods.paypal.local.payPalClientId ||
        payoutMethods.paypal.local.payPalSecret ||
        payoutMethods.paypal.local.payPalEnabled)
    ) {
      payoutMethods.paypal.toggleErrors(true);
      const validationErrors = Object.values(payoutMethods.paypal.validation).filter(
        (err) => !!err
      );
      if (validationErrors.length > 0) return;

      update.payPalClientId = payoutMethods.paypal.local.payPalClientId;
      update.payPalSecret = payoutMethods.paypal.local.payPalSecret;
      update.payPalEnabled = payoutMethods.paypal.local.payPalEnabled;
    }

    if (
      payoutMethods.iban.ui.edit &&
      (payoutMethods.iban.local.ibanEnabled ||
        !!payoutMethods.iban.local.iban ||
        !!payoutMethods.iban.local.accountHolder)
    ) {
      payoutMethods.iban.toggleErrors(true);
      const validationErrors = Object.values(payoutMethods.iban.validation).filter((err) => !!err);
      if (validationErrors.length > 0) return;

      update.ibanNew = payoutMethods.iban.local.iban;
      update.accountHolder = payoutMethods.iban.local.accountHolder;
      update.ibanEnabled = payoutMethods.iban.local.ibanEnabled;
    }

    payoutMethods.setSubmitting(true);
    let updatedOrCreatedPayoutAccount: IPayoutAccount;
    let response;
    if (payoutMethods.payoutAccountId) {
      response = await updatePayoutAccount(
        { id: payoutMethods.payoutAccountId, ...update },
        appDispatch
      );
    } else {
      response = await createPayoutAccount(
        {
          ibanNew: '',
          accountHolder: '',
          payPalClientId: '',
          payPalSecret: '',
          payPalEnabled: false,
          ibanEnabled: false,
          ...update
        },
        appDispatch
      );
    }
    if (response.result) {
      updatedOrCreatedPayoutAccount = response.result;
    } else if (response.error?.name === 'PayPalCredsAreInvalidError') {
      payoutMethods.paypal.setValidation({
        payPalSecret: getTranslationKey('shop.settings.payPalCredsInvalid')
      });
    }
    payoutMethods.setSubmitting(false);

    if (canEnablePayPal(updatedOrCreatedPayoutAccount)) {
      payoutMethods.paypal.toggleEditMode(false);
    }

    if (canEnableIBAN(updatedOrCreatedPayoutAccount)) {
      payoutMethods.iban.toggleEditMode(false);
    }
  }, [payoutMethods]);

  return { ...payoutMethods, submit };
}

export function isActivatingPaypalAllowed(
  state: PayoutMethodsFormState,
  payoutAccount: IPayoutAccount,
  payoutMethod: 'paypal' | 'iban'
): boolean {
  if (payoutMethod === 'paypal') {
    return !(!!state.paypal.validation.payPalClientId || !!state.paypal.validation.payPalSecret);
  }
  if (payoutMethod === 'iban') {
    return !(
      (!state.iban.local.iban && !payoutAccount.ibanNew) ||
      !!state.iban.validation.accountHolder
    );
  }
}
